/**
 * Transport helpers that use sendBeacon as an unconfirmed best-effort send and
 * use fetch responses for confirmed delivery.
 */
import { sleep } from "./utils";

export type TransportOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
  useBeacon?: boolean;
  beforeAttempt?: (attempt: number) => boolean | Promise<boolean>;
  captureJsonResponse?: boolean;
};

export type TransportResult = {
  ok: boolean;
  status?: number;
  beaconAccepted?: boolean;
  cancelled?: boolean;
  responseBody?: unknown;
};

export async function postJson(
  url: string,
  payload: unknown,
  options: TransportOptions = {},
): Promise<TransportResult> {
  const body = JSON.stringify(payload);
  const useBeacon =
    options.useBeacon &&
    typeof navigator !== "undefined" &&
    navigator.sendBeacon;

  if (useBeacon) {
    try {
      const ok = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" }),
      );
      if (ok) return { ok: false, beaconAccepted: true };
    } catch {
      // fall through
    }
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      // Allow first-party cookie workflows (e.g. /pxl/firstparty) and
      // same-site tracking when the ingest domain is a CNAME under the site.
      credentials: "include",
    });
    let responseBody: unknown;
    if (
      options.captureJsonResponse &&
      typeof (response as { json?: unknown }).json === "function"
    ) {
      try {
        responseBody = await response.json();
      } catch {
        // A missing/malformed bounded control response is handled fail-closed
        // by its caller.
      }
    }
    return {
      ok: response.ok,
      status: response.status,
      ...(responseBody !== undefined ? { responseBody } : {}),
    };
  } catch {
    return { ok: false };
  }
}

export async function postWithRetry(
  url: string,
  payload: unknown,
  options: TransportOptions = {},
): Promise<boolean> {
  return (await postWithRetryResult(url, payload, options)).ok;
}

export async function postWithRetryResult(
  url: string,
  payload: unknown,
  options: TransportOptions = {},
): Promise<TransportResult> {
  const maxAttempts = options.maxAttempts ?? 3;
  let delay = options.baseDelayMs ?? 500;
  let lastResult: TransportResult = { ok: false };

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options.beforeAttempt) {
      try {
        if (!(await options.beforeAttempt(attempt))) {
          return { ...lastResult, cancelled: true };
        }
      } catch {
        return { ...lastResult, cancelled: true };
      }
    }
    const result = await postJson(url, payload, options);
    lastResult = result;
    if (result.ok || result.beaconAccepted) return result;
    if (
      result.status !== undefined &&
      result.status !== 408 &&
      result.status !== 425 &&
      result.status !== 429 &&
      result.status < 500
    ) {
      return result;
    }
    if (attempt === maxAttempts - 1) break;
    await sleep(delay);
    delay = Math.min(delay * 2, 5000);
  }
  return lastResult;
}
